import React, { useMemo } from 'react';
import useCurrentUser from '@API/auth/useCurrentUser';
import {
  MANAGEMENT_PERMISSIONS,
  MANAGEMENT_PERMISSIONS_SERVER
} from '@shared/constants/permissions';
import useUserPermission from '@shared/api/useUserPermission';
import { DSColors } from '@design-system-outdated/constants';
import useWorkspaceUsersPermissions from '@shared/api/useWorkspaceUsersPermissions';
import {
  getPermissionValueByType,
  getUserPermissionsByUserName,
  getUserUniqueName,
  ifPermissionValueTruthful
} from '@shared/utils/permissions';
import { APPLICATION_TYPE, LLM_APPLICATION_URL } from '@shared/constants';
import Card from './Card';
import WorkspaceCardFooter from './WorkspaceCardFooter';
import { WorkspaceCardProps } from './WorkspaceCard.type';
import { generateMoreMenuOptions } from './helpers';

const AMOUNT_OF_SHOWN_MEMBERS = 4;

const WorkspaceCard: React.FC<WorkspaceCardProps> = ({
  workspaceId,
  workspaceName,
  workspaceStorageUsage = 0,
  members = [],
  createdBy,
  createdAt,
  isDefault,
  membersCount = 0,
  projectsCount,
  isAdmin,
  isMember,
  disabledInviteButton,
  onLoadMenuMembers,
  onClickExtraMenu,
  onClickInvite
}) => {
  const { data: currentUser } = useCurrentUser();
  const { getPermissionStatus } = useUserPermission();
  const ifCanRemoveUsersFromWs =
    getPermissionStatus({
      workspaceName,
      permissionKey: MANAGEMENT_PERMISSIONS.REMOVE_YOURSELF_FROM_WORKSPACE
    }) ||
    getPermissionStatus({
      workspaceName,
      permissionKey: MANAGEMENT_PERMISSIONS.REMOVE_USERS_FROM_WORKSPACE
    });
  const ifCanChangeWsRoles =
    getPermissionStatus({
      workspaceName,
      permissionKey: MANAGEMENT_PERMISSIONS.CHANGE_WORKSPACE_ROLE
    }) ||
    getPermissionStatus({
      workspaceName,
      permissionKey: MANAGEMENT_PERMISSIONS.CHANGE_WORKSPACE_ROLE_FOR_YOURSELF
    });
  const ifCanInviteUsers =
    getPermissionStatus({
      workspaceName,
      permissionKey: MANAGEMENT_PERMISSIONS.INVITE_USERS_FROM_ORGANIZATION
    }) &&
    getPermissionStatus({
      workspaceName,
      permissionKey: MANAGEMENT_PERMISSIONS.INVITE_USERS_OUT_OF_ORGANIZATION
    });
  const { data: permissions = [] } = useWorkspaceUsersPermissions(workspaceId, {
    enabled: ifCanInviteUsers || ifCanChangeWsRoles || ifCanRemoveUsersFromWs
  });
  const membersWithAdminFlag = useMemo(
    () =>
      members.map(member => {
        const uniqueName = member.isMember
          ? getUserUniqueName(member)
          : member.email;
        return {
          ...member,
          isWsOwner: ifPermissionValueTruthful(
            getPermissionValueByType(
              getUserPermissionsByUserName(permissions, uniqueName),
              MANAGEMENT_PERMISSIONS_SERVER.MANAGEMENT
            )
          ),
          isAdmin: uniqueName === getUserUniqueName(currentUser)
        };
      }),
    [members, permissions, currentUser]
  );
  const membersPreview = useMemo(
    () => (membersWithAdminFlag || []).slice(0, AMOUNT_OF_SHOWN_MEMBERS),
    [membersWithAdminFlag]
  );

  const {
    isOwner: ifCurrentUserWsOwner,
    isMember: ifCurrentUserWsMember
  } = useMemo(() => {
    let isOwner = false;
    let isMember = false;
    (membersWithAdminFlag || []).some(member => {
      if (member.username === currentUser?.username) {
        isMember = true;
        if (member.isWsOwner) {
          isOwner = true;
        }
        return true;
      }
    });
    return {
      isMember,
      isOwner
    };
  }, [currentUser, membersWithAdminFlag]);
  const menuMembers = useMemo(() => {
    return (membersWithAdminFlag || []).slice(membersPreview.length);
  }, [membersWithAdminFlag, membersPreview]);

  const getWorkspaceURL = () => {
    if (currentUser?.userAppType === APPLICATION_TYPE.em) {
      return `${window.location.origin}/${workspaceName?.toLowerCase()}`;
    }

    const baseURL = new URL(
      `${LLM_APPLICATION_URL}${workspaceName?.toLowerCase()}`,
      window.location.origin
    );

    return baseURL.href;
  };

  const tags = useMemo(() => {
    const retVal = [];

    if (isMember && !ifCurrentUserWsOwner) {
      retVal.push({
        label: 'Member',
        color: DSColors.grayColor2,
        textColor: DSColors.grayColor6
      });
    }

    if (ifCurrentUserWsOwner) {
      retVal.push({ label: 'Owner', color: DSColors.orangeColor });
    }

    return retVal;
  }, [ifCurrentUserWsOwner, isMember]);

  return (
    <Card
      name={workspaceName}
      nameMaskTest="workspace"
      membersPreview={membersPreview}
      menuMembers={menuMembers}
      membersCount={membersCount}
      isMember={isMember}
      onLoadMenuMembers={onLoadMenuMembers}
      moreMenuOptions={generateMoreMenuOptions({
        isDefault,
        membersCount,
        isAdmin,
        isMember: ifCurrentUserWsMember,
        canManageUsers: ifCanChangeWsRoles
      })}
      isDefault={isDefault}
      tags={tags}
      cardFooter={
        <WorkspaceCardFooter
          workspaceStorageUsage={workspaceStorageUsage}
          projectsCount={projectsCount}
          createdAt={createdAt}
          createdBy={createdBy}
          workspaceUrl={getWorkspaceURL()}
        />
      }
      onClickMenuOption={onClickExtraMenu}
      onClickInvite={onClickInvite}
      disabledInviteButton={disabledInviteButton}
      tooltipTextInviteButton={
        disabledInviteButton ? 'Only workspace members can invite users' : ''
      }
    />
  );
};

export default WorkspaceCard;
